import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SpeechCard from './SpeechCard';
import LibraryFilters from './LibraryFilters';
import AddLibraryVideoModal from './AddLibraryVideoModal';
import UploadMyVideoModal from './UploadMyVideoModal';
import ConfirmDialog from './ConfirmDialog';
import Larry from './Larry';
import { getSpeeches, getCurrentMember, deleteSpeech, requestFriend, getFriendRequests } from '../services/api';
import usePageTitle from '../hooks/usePageTitle';

const GHOST_BUTTON = { background: 'var(--bg-input)', border: '1px solid var(--border-subtle)', color: 'var(--text-secondary)' };

// Categories were dropped in the 2026-08 pivot: the Library is now one flat,
// recency-ordered feed with search + tag filtering. Tags (speeches.tags) carry
// any grouping the club wants. Multi-library "spaces" is a deliberate next phase.

export default function BrowseSpeeches() {
  usePageTitle('Speech Library');
  const [speeches, setSpeeches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [allTags, setAllTags] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const navigate = useNavigate();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  // speaker member_id -> 'sent' | 'pending' | 'error'. Seeded from the outgoing
  // requests already in flight so a reload doesn't offer "Ask" again for someone
  // you already asked (the POST would just 409 REQUEST_EXISTS).
  const [requestStates, setRequestStates] = useState({});
  const canSeeAllRef = useRef(false);
  const isAdminRef = useRef(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    getCurrentMember()
      .then((res) => {
        const role = res?.member?.role;
        isAdminRef.current = role === 'admin';
        if (role === 'admin' || role === 'evaluator') {
          canSeeAllRef.current = true;
        }
      })
      .catch(() => {})
      .finally(() => { loadSpeeches(); loadOutgoingRequests(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSpeeches = async () => {
    setLoading(true);
    setError(null);
    try {
      const type = canSeeAllRef.current ? 'all' : 'public';
      const res = await getSpeeches(type, 200, 0);
      if (res?.speeches) {
        setSpeeches(res.speeches);
        const tags = [...new Set(res.speeches.flatMap(s => s.tags || []))].sort();
        setAllTags(tags);
      }
    } catch (err) {
      console.error('[BrowseSpeeches] Load error:', err);
      setError(err.message || 'Failed to load speeches');
    } finally {
      setLoading(false);
    }
  };

  const loadOutgoingRequests = async () => {
    try {
      const res = await getFriendRequests({ direction: 'outgoing' });
      const next = {};
      (res?.requests || []).forEach((r) => { next[String(r.member_id)] = 'pending'; });
      setRequestStates(prev => ({ ...next, ...prev }));
    } catch (_) {
      // Non-fatal: the worst case is offering "Ask" for someone already asked,
      // which the backend rejects with a clear 409 anyway.
    }
  };

  const handleRequestFriend = async (speakerId) => {
    if (!speakerId) return;
    const key = String(speakerId);
    try {
      await requestFriend(key);
      setRequestStates(prev => ({ ...prev, [key]: 'sent' }));
    } catch (err) {
      // A duplicate isn't a failure from the user's point of view — they already
      // asked, so show the truthful "waiting" state rather than an error.
      const already = err.code === 'REQUEST_EXISTS' || err.code === 'ALREADY_FRIENDS';
      setRequestStates(prev => ({ ...prev, [key]: already ? 'pending' : 'error' }));
    }
  };

  // Admin-only, mirroring DELETE /speeches/:id. The old owner-can-delete-their-own
  // 'dry_run' carve-out went with the category field.
  const canDelete = () => isAdminRef.current;

  const handleDeleteConfirmed = async () => {
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      await deleteSpeech(id);
      loadSpeeches();
    } catch (err) {
      setError(err.message || 'Failed to delete video');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse h-10 rounded-xl" style={{ background: 'var(--bg-input)' }} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {[0, 1, 2, 3].map(i => (
            <div key={i} className="animate-pulse h-40 rounded-2xl" style={{ background: 'var(--bg-input)' }} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-16 gap-4">
        <Larry mood="sad" size={64} message="Couldn't load speeches!" />
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{error}</p>
        <button onClick={loadSpeeches} className="btn-bouncy text-sm px-5 py-2">
          Try Again
        </button>
      </div>
    );
  }

  const q = search.toLowerCase().trim();
  const filtered = speeches
    .filter(s =>
      (!q || s.title?.toLowerCase().includes(q) || s.speaker_name?.toLowerCase().includes(q)) &&
      (!activeTag || (s.tags || []).includes(activeTag))
    )
    .sort((a, b) => (Number(b.created_at) || 0) - (Number(a.created_at) || 0));

  return (
    <div className="space-y-6 stagger-in">
      <h1 className="sr-only">Library</h1>

      <div className="flex items-start justify-between gap-3">
        <LibraryFilters
          search={search} onSearchChange={setSearch}
          allTags={allTags} activeTag={activeTag} onTagChange={setActiveTag}
        />
        <div className="flex gap-2 flex-shrink-0">
          {isAdminRef.current && (
            <button onClick={() => setShowAddModal(true)} className="text-sm px-4 py-2 rounded-lg" style={GHOST_BUTTON}>+ Add YouTube</button>
          )}
          <button onClick={() => setShowUploadModal(true)} className="btn-bouncy text-sm px-4 py-2">
            Upload a video
          </button>
        </div>
      </div>

      {speeches.length === 0 ? (
        <div className="card-paper p-8 flex flex-col items-center text-center gap-3">
          <Larry mood="excited" size={64} message="No speeches yet — be the first!" />
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-center py-8" style={{ color: 'var(--text-muted)' }}>
          No speeches match your filter
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {filtered.map(speech => (
            <SpeechCard
              key={speech.speech_id}
              speech={speech}
              showSpeaker
              onDelete={canDelete() ? () => setConfirmDeleteId(speech.speech_id) : undefined}
              onRequestFriend={handleRequestFriend}
              friendRequestState={requestStates[String(speech.speaker_id)] || 'none'}
            />
          ))}
        </div>
      )}

      {showAddModal && (
        <AddLibraryVideoModal
          onCreated={() => { setShowAddModal(false); loadSpeeches(); }}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {showUploadModal && (
        <UploadMyVideoModal
          onUploaded={(speechId) => { setShowUploadModal(false); navigate(`/speeches/${speechId}`); }}
          onClose={() => setShowUploadModal(false)}
        />
      )}

      {confirmDeleteId && (
        <ConfirmDialog
          message="Delete this video? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleDeleteConfirmed}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
    </div>
  );
}
